import {
  Component,
  NgZone,
  OnInit,
  EnvironmentInjector,
  runInInjectionContext,
  ViewEncapsulation,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import moment from 'moment';
import { Observable, of, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap, distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { ChargingTransaction } from '../charging-station-location/charging-station-location-model';
import { AppState } from 'src/app/app.state';
import { environment } from 'src/environments/environment';
import { FormControl, FormGroup } from '@angular/forms';
import { Organization } from '../organization/organization-models';
import { Firestore, collection, doc, query, where, limit, documentId, DocumentChange } from '@firebase/firestore';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { FIRESTORE_INSTANCE } from 'src/app/shared/services/firestore-instance.token';
import { observeCollectionChanges, observeDocData } from 'src/app/shared/services/firestore-observables';

@Component({
  selector: 'adm-live-charging-dashboard',
  templateUrl: './live-charging-dashboard.component.html',
  styleUrls: ['./live-charging-dashboard.component.scss'],
  imports: [MatAccordion, MatExpansionModule, AsyncPipe, DatePipe, DecimalPipe, FeedbackInfoComponent],
  encapsulation: ViewEncapsulation.None, // ← ensure CSS applies to dynamic elements
})
export class LiveChargingDashboardComponent implements OnInit {
  public moment = moment;
  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });

  // All transactions
  public allTransactions$: Observable<ChargingTransaction[]>;

  // Filtered streams
  public activeChargingTransactions$: Observable<ChargingTransaction[]>;
  public finishedChargingTransactions$: Observable<ChargingTransaction[]>;
  private finishedMarkers: Record<string, google.maps.marker.AdvancedMarkerElement> = {};
  private markerLibrary: google.maps.MarkerLibrary;

  // Hour range filter
  public selectedHourRange = 24;
  public selectedOrganizationId = null;
  private finishedHourRange$: Observable<number>;
  private transactionsService = inject(TransactionsService);
  private firestore = inject<Firestore>(FIRESTORE_INSTANCE);
  private ngZone = inject(NgZone);
  public appState = inject(AppState);
  private injector = inject(EnvironmentInjector);

  // Live Firestore documents for active tokens
  public liveChargingChanges$: Observable<DocumentChange[]>;
  public selectedTransaction: ChargingTransaction | null = null;

  public formGroup: FormGroup<FilterFormGroup> = new FormGroup<FilterFormGroup>({
    hours: new FormControl<number>(1, { nonNullable: true }),
    selectedOrganizationId: new FormControl<number | null>(null),
  });
  public organizationTypeAheadModel: FormGroup = new FormGroup({
    typeAheadQuery: new FormControl<string | null | { id: string; name: string }>(''),
  });
  get organizationTypeAheadQuery() {
    return this.organizationTypeAheadModel.controls['typeAheadQuery'];
  }

  // Google Map and markers
  private map: google.maps.Map;
  private markers: GoogleMarker[] = [];
  private markerMap: Record<string, GoogleMarker> = {};

  constructor() {
    // Observable for hour range
    this.finishedHourRange$ = of(this.selectedHourRange);
  }

  ngOnInit(): void {
    this.moment.locale('da');

    this.setupTransactionStreams();

    setTimeout(() => {
      this.initMap();
      this.subscribeToActiveTransactions();
    }, 100);
  }

  public organizationSelected(org: Organization | null) {
    this.formGroup.patchValue({
      selectedOrganizationId: org ? org.id : null,
    });
    this.organizationTypeAheadQuery.setValue({ id: org?.id, name: org?.name });
  }

  public async onFinishedOpened(tx: ChargingTransaction): Promise<void> {
    // If there’s already a marker, do nothing
    if (this.finishedMarkers[tx.chargingToken]) {
      return;
    }
    // Create a small DIV container for the orange icon:
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'relative';
    markerDiv.style.width = '30px';
    markerDiv.style.height = '30px';
    markerDiv.style.cursor = 'pointer';

    if (tx.organizationCompactLogo) {
      markerDiv.innerHTML = `
      <img
        src="${tx.organizationCompactLogo}"
        alt="${tx.organizationName}"
        style="
          width: 100%;
          height: 100%;
          object-fit: contain;
          border-radius: 50%;
          border: 2px solid white;
          box-shadow: 0 0 5px rgba(0,0,0,0.5);
          background-color: white;
        "
      />
    `;
    } else {
      markerDiv.innerHTML = `
      <img
        src="/assets/icons/charging_orange.svg"
        alt="Charging"
        style="
          width: 100%;
          height: 100%;
        "
      />
    `;
    }

    // Place the marker on the map at tx.latitude/tx.longitude
    const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    const marker = new AdvancedMarkerElement({
      map: this.map,
      position: { lat: tx.latitude, lng: tx.longitude },
      content: markerDiv,
    });

    // Save so we can remove it later
    this.finishedMarkers[tx.chargingToken] = marker;
  }

  /**
   * Called when a finished‐charging panel is collapsed.
   * Removes the orange marker from the map.
   */
  public onFinishedClosed(tx: ChargingTransaction): void {
    const marker = this.finishedMarkers[tx.chargingToken];
    if (marker) {
      marker.map = null; // remove from map
      delete this.finishedMarkers[tx.chargingToken];
    }
  }

  //
  // 1) Set up RxJS streams for transactions (active vs. finished)
  //
  private setupTransactionStreams(): void {
    const formValue$ = this.formGroup.valueChanges.pipe(
      startWith(this.formGroup.value), // so it runs immediately
      distinctUntilChanged((a, b) => a.hours === b.hours && a.selectedOrganizationId === b.selectedOrganizationId)
    );

    const allTransactions$ = formValue$.pipe(
      switchMap(() => this.transactionsService.getTransactions()),
      map(result => (Array.isArray(result) ? result : [])),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.allTransactions$ = allTransactions$;

    this.activeChargingTransactions$ = allTransactions$.pipe(
      map(transactions => transactions.filter(tx => tx.isCharging)),
      distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((v, i) => v.chargingToken === curr[i].chargingToken))
    );

    this.finishedChargingTransactions$ = allTransactions$.pipe(map(transactions => transactions.filter(tx => !tx.isCharging)));

    // Call setupLiveChargingChanges and subscribeToActiveTransactions after both observables are set up
    // Wait for the first emission of both
    combineLatest([this.activeChargingTransactions$, this.finishedChargingTransactions$])
      .pipe(take(1))
      .subscribe(() => {
        this.setupLiveChargingChanges();
        this.subscribeToActiveTransactions();
      });
  }

  //
  // 2) Watch activeChargingTransactions$ → extract tokens, then query Firestore for live docs
  //
  private setupLiveChargingChanges(): void {
    const activeTokens$ = this.activeChargingTransactions$.pipe(
      map(activeTxs => activeTxs.map(tx => tx.chargingToken.toString())),
      distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((v, i) => v === curr[i]))
    );

    this.liveChargingChanges$ = activeTokens$.pipe(
      switchMap(tokens => {
        if (!tokens.length) {
          return of([]); // Empty array of DocumentChangeAction<any>
        }

        // Firestore “in” query chunks (max 10 IDs each)
        const chunks: string[][] = [];
        for (let i = 0; i < tokens.length; i += 10) {
          chunks.push(tokens.slice(i, i + 10));
        }

        const changeStreams = chunks.map(chunk =>
          runInInjectionContext(this.injector, () => {
            const remoteTxRef = collection(this.firestore, 'OK/Emsp/RemoteTransactions');
            const q = query(remoteTxRef, where(documentId(), 'in', chunk), limit(1000));
            return observeCollectionChanges(q, this.ngZone);
          })
        );

        return combineLatest(changeStreams).pipe(map(arrays => arrays.flat()));
      })
    );

    // React only to 'modified' docs to animate the corresponding marker
    this.liveChargingChanges$.subscribe(changes => {
      changes.forEach(change => {
        if (change.type === 'modified') {
          const docId = change.doc.id;
          const data = change.doc.data() as any;
          const marker = this.findMarkerById(docId);
          if (marker) {
            this.animateMarker(marker, data.chargeInWh, data.powerUpdated, data.totalConsumptionInWh, data.powerInW);
          }
        }
      });
    });
  }

  //
  // 3) Initialize Google Map and place markers for active transactions
  //
  private async initMap(): Promise<void> {
    const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
    const markerLib = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
    this.markerLibrary = markerLib;

    this.map = new Map(document.getElementById('active-map') as HTMLElement, {
      zoom: 7.9,
      center: { lat: 56.2639, lng: 10.318 },
      mapId: environment.googleMapId,
      disableDefaultUI: true,
      clickableIcons: false,
    });
  }

  private subscribeToActiveTransactions(): void {
    if (!this.markerLibrary) {
      console.warn('Marker library not loaded yet, deferring marker subscription');
      setTimeout(() => this.subscribeToActiveTransactions(), 100);
      return;
    }

    const { AdvancedMarkerElement } = this.markerLibrary;

    this.activeChargingTransactions$.subscribe(transactions => {
      this.clearAllMarkers();
      transactions.forEach(tx => {
        const markerDiv = this.createCustomMarkerElement(tx);
        const marker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position: { lat: tx.latitude, lng: tx.longitude },
          content: markerDiv,
          gmpClickable: true,
        });

        marker.addEventListener('gmp-click', () => this.onMarkerClick(tx));

        this.markers.push(marker);
        this.markerMap[tx.chargingToken] = marker;
      });
    });
  }
  //
  // Helper: Create a custom HTML element for the marker
  //
  private createCustomMarkerElement(tx: ChargingTransaction): HTMLElement {
    // Create a wrapper DIV
    const markerDiv = document.createElement('div');
    markerDiv.style.position = 'relative';
    markerDiv.style.width = '28px';
    markerDiv.style.height = '28px';
    markerDiv.style.cursor = 'pointer';

    // Tag the <img> with a CSS class "charging-marker" for the pulse animation
    markerDiv.innerHTML = `
      <div class="marker-icon-container">
        <img
          src="/assets/icons/charging_green.svg"
          class="charging-marker"
          alt="EV Charging"
          style="
            width: 100%;
            height: 100%;
          "
        />
      </div>
    `;
    return markerDiv;
  }

  private onMarkerClick(tx: ChargingTransaction): void {
    // Fetch Firestore doc for this chargingToken
    runInInjectionContext(this.injector, () => {
      const docRef = doc(this.firestore, 'OK/Emsp/RemoteTransactions', tx.chargingToken);

      observeDocData(docRef, this.ngZone).subscribe(doc => {
        if (doc) {
          // Merge liveDoc data into tx and store in selectedTransaction
          tx.liveDoc = {
            id: tx.chargingToken,
            chargeInWh: (doc as any).chargeInWh,
            powerUpdated: (doc as any).powerUpdated?.toDate(),
            totalConsumptionInWh: (doc as any).totalConsumptionInWh,
            powerInW: (doc as any).powerInW,
            fireStoreDocLoaded: true,
            // …any other fields you want…
          };
          this.selectedTransaction = tx;
        } else {
          tx.liveDoc = {
            id: tx.chargingToken,
            chargeInWh: 0,
            powerUpdated: new Date(0),
            totalConsumptionInWh: 0,
            powerInW: 0,
            fireStoreDocLoaded: false,
          };
          this.selectedTransaction = tx;
        }
      });
    });
  }

  //
  // Helper: Find the AdvancedMarkerElement by chargingToken
  //
  private findMarkerById(token: string): GoogleMarker | undefined {
    return this.markerMap[token];
  }

  //
  // Helper: Animate a marker when live updates arrive
  //
  private animateMarker(
    marker: google.maps.marker.AdvancedMarkerElement,
    chargeInWh: number,
    powerUpdated: number,
    totalConsumptionInWh: number,
    powerInW: number
  ): void {
    // Find the <img class="charging-marker"> inside the marker’s content
    const el = marker.content as HTMLElement;
    if (!el) return;

    const img = el.querySelector('img.charging-marker') as HTMLImageElement;
    if (!img) return;

    // 1) Temporarily swap to orange icon
    img.src = '/assets/icons/charging_orange.svg';

    // 2) Add the quick-pulse class so it does one orange pulse
    img.classList.add('quick-pulse');

    // 3) After 1 second (duration of quick-pulse), remove class and revert src
    setTimeout(() => {
      img.classList.remove('quick-pulse');
      img.src = '/assets/icons/charging_green.svg';
      // The green icon’s infinite pulse (via .charging-marker CSS) continues automatically
    }, 1000);
  }

  //
  // Helper: Remove all markers from the map and clear arrays/maps
  //
  private clearAllMarkers(): void {
    this.markers.forEach(marker => {
      // Remove from map by setting map property to null
      marker.map = null;
    });
    this.markers = [];
    this.markerMap = {};
  }

  public secondsToPrettyTime(seconds: number | null): string {
    if (typeof seconds !== 'number' || isNaN(seconds)) {
      return 'Ukendt ladetid';
    }
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    const parts: string[] = [];
    if (hrs > 0) parts.push(`${hrs} t.`);
    if (mins > 0) parts.push(`${mins} min.`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs} sek.`);
    return parts.join(' ');
  }
  public resetOrganization() {
    this.organizationTypeAheadQuery.setValue(null);
    this.formGroup.patchValue({
      selectedOrganizationId: null,
    });
  }

  public consumptionToPrettyOutput(consumption: number | null): string {
    if (consumption === null || isNaN(consumption)) {
      return 'Ukendt forbrug';
    }
    if (consumption >= 1000) {
      return (consumption / 1000).toFixed(0) + ' kWh';
    } else {
      return consumption.toFixed(0) + ' Wh';
    }
  }
}

// Type alias for AdvancedMarkerElement (to keep TS happy)
type GoogleMarker = google.maps.marker.AdvancedMarkerElement;
interface FilterFormGroup {
  hours: FormControl<number>;
  selectedOrganizationId: FormControl<number | null>;
}
